import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  UserCog,
  Package,
  ShoppingCart,
  ClipboardList,
  Users,
  BarChart3,
  Settings,
  ChevronRight,
  ChevronDown,
  BookOpen,
} from "lucide-react";

const Sidebar = ({ isOpen, onClose }) => {
  const [profileOpen, setProfileOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [clientsOpen, setClientsOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [booksOpen, setBooksOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setProfileOpen(location.pathname.startsWith("/profile"));
  }, [location.pathname]);

  useEffect(() => {
    setProductOpen(location.pathname.startsWith("/manage-products"));
  }, [location.pathname]);
  useEffect(() => {
    setOrdersOpen(location.pathname.startsWith("/orders"));
  }, [location.pathname]);
  useEffect(() => {
    setClientsOpen(location.pathname.startsWith("/manage-client"));
  }, [location.pathname]);
  useEffect(() => {
    setDeliveryOpen(location.pathname.startsWith("/agent"));
  }, [location.pathname]);
  useEffect(() => {
    setReportsOpen(location.pathname.startsWith("/reports"));
  }, [location.pathname]);
  useEffect(() => {
    setBooksOpen(location.pathname.startsWith("/books"));
  }, [location.pathname]);
  useEffect(() => {
    setSettingsOpen(location.pathname.startsWith("/settings"));
  }, [location.pathname]);

  const linkClass = ({ isActive }) =>
    `block px-4 py-2 rounded-md text-sm transition-all duration-200 mx-0
   ${
     isActive
       ? "bg-amber-100 text-blue-900 font-medium border-l-2 border-amber-500"
       : "text-blue-900 hover:bg-slate-100 hover:text-blue-900"
   }`;

  const handleMenuToggle = (menu) => {
    if (menu === "profile") {
      setProfileOpen(!profileOpen);
      setProductOpen(false);
      setOrdersOpen(false);
      setClientsOpen(false);
      setDeliveryOpen(false);
      setReportsOpen(false);
    } else if (menu === "product") {
      setProductOpen(!productOpen);
      setProfileOpen(false);
      setOrdersOpen(false);
      setClientsOpen(false);
      setDeliveryOpen(false);
      setReportsOpen(false);
    } else if (menu === "orders") {
      setOrdersOpen(!ordersOpen);
      setProfileOpen(false);
      setProductOpen(false);
      setClientsOpen(false);
      setDeliveryOpen(false);
      setReportsOpen(false);
    } else if (menu === "clients") {
      setClientsOpen(!clientsOpen);
      setProfileOpen(false);
      setProductOpen(false);
      setOrdersOpen(false);
      setDeliveryOpen(false);
      setReportsOpen(false);
    } else if (menu === "agent") {
      setDeliveryOpen(!deliveryOpen);
      setProfileOpen(false);
      setProductOpen(false);
      setOrdersOpen(false);
      setClientsOpen(false);
      setReportsOpen(false);
    } else if (menu === "reports") {
      setReportsOpen(!reportsOpen);
      setProfileOpen(false);
      setProductOpen(false);
      setOrdersOpen(false);
      setClientsOpen(false);
      setDeliveryOpen(false);
      setBooksOpen(false);
    } else if (menu === "books") {
      setBooksOpen(!booksOpen);
      setReportsOpen(false);
      setProfileOpen(false);
      setProductOpen(false);
      setOrdersOpen(false);
      setClientsOpen(false);
      setDeliveryOpen(false);
    } else if (menu === "settings") {
      setSettingsOpen(!settingsOpen);
      setReportsOpen(false);
      setBooksOpen(false);
      setProfileOpen(false);
      setProductOpen(false);
      setOrdersOpen(false);
      setClientsOpen(false);
      setDeliveryOpen(false);
    }
  };
  const user = JSON.parse(localStorage.getItem("user"));
  const isSuperAdmin = user?.role === "superAdmin";
  const isAdmin = user?.role === "admin";

  return (
    <>
      {/* OVERLAY - Only below 1024px */}
      {isOpen && (
        <div
          onClick={onClose}
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
        />
      )}

      <aside
        className={`
          fixed top-16 left-0 w-64 h-[calc(100vh-64px)] overflow-y-auto
          bg-white text-gray-700 z-40 border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0 lg:block
          [&::-webkit-scrollbar]:hidden overscroll-contain
        `}
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <nav className="py-4 space-y-1 px-0">
          {/* DASHBOARD – ALL */}
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              `flex items-center gap-3 w-full px-4 py-2.5 rounded-none text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-blue-50 text-blue-900 border-l-4"
                  : "text-gray-700 hover:bg-gray-50"
              }`
            }
            style={({ isActive }) =>
              isActive ? { borderLeftColor: "#172554" } : {}
            }
          >
            <LayoutDashboard size={18} />
            <span>Dashboard</span>
          </NavLink>

          {/* ================= SUPER ADMIN ONLY ================= */}
          {isSuperAdmin && (
            <>
              {/* MANAGE ADMIN */}
              <button
                onClick={() => handleMenuToggle("profile")}
                className={`w-full px-4 py-2.5 rounded-none flex justify-between items-center transition text-sm font-medium
          ${
            profileOpen
              ? "bg-blue-50 text-blue-900"
              : "text-gray-700 hover:bg-gray-50"
          }
        `}
              >
                <div className="flex items-center gap-3">
                  <UserCog size={18} />
                  <span>Manage Admin</span>
                </div>
                {profileOpen ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </button>

              <div
                className={`ml-6 border-l border-gray-200 transition-all duration-300 ease-in-out overflow-hidden ${
                  profileOpen ? "max-h-24 opacity-100 mt-0" : "max-h-0 opacity-0 pointer-events-none"
                }`}
              >
                <NavLink
                  to="/profile/alladmin"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                      isActive
                        ? "bg-blue-50 text-blue-900 border-l-4"
                        : "text-gray-700 hover:bg-gray-50"
                    }`
                  }
                  style={({ isActive }) =>
                    isActive ? { borderLeftColor: "#172554" } : {}
                  }
                >
                  All Admin
                </NavLink>
                <NavLink
                  to="/profile/add"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                      isActive
                        ? "bg-blue-50 text-blue-900 border-l-4"
                        : "text-gray-700 hover:bg-gray-50"
                    }`
                  }
                  style={({ isActive }) =>
                    isActive ? { borderLeftColor: "#172554" } : {}
                  }
                >
                  Add Admin
                </NavLink>
              </div>
            </>
          )}

          {/* ================= PRODUCTS – ADMIN + SUPER ADMIN ================= */}
          <button
            onClick={() => handleMenuToggle("product")}
            className={`w-full px-4 py-2.5 rounded-none flex justify-between items-center transition text-sm font-medium
      ${
        productOpen
          ? "bg-blue-50 text-blue-900"
          : "text-gray-700 hover:bg-gray-50"
      }
    `}
          >
            <div className="flex items-center gap-3">
              <Package size={18} />
              <span>Manage Products</span>
            </div>
            {productOpen ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </button>

          <div
            className={`ml-6 border-l border-gray-200 transition-all duration-300 ease-in-out overflow-hidden ${
              productOpen ? "max-h-60 opacity-100 mt-0" : "max-h-0 opacity-0 pointer-events-none"
            }`}
          >
            <NavLink
              to="/manage-products/brands"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              Brands
            </NavLink>
            <NavLink
              to="/manage-products/categories"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              Category
            </NavLink>
            <NavLink
              to="/manage-products/products"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              Products
            </NavLink>
            <NavLink
              to="/manage-products/location"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              Warehouse Locations
            </NavLink>
            <NavLink
              to="/manage-products/restock-history"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              Restock History
            </NavLink>
          </div>
          <NavLink
            to="/pos"
            className={({ isActive }) =>
              `flex items-center gap-3 w-full px-4 py-2.5 rounded-none text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-blue-50 text-blue-900 border-l-4"
                  : "text-gray-700 hover:bg-gray-50"
              }`
            }
            style={({ isActive }) =>
              isActive ? { borderLeftColor: "#172554" } : {}
            }
          >
            <ShoppingCart size={18} />
            <span>POS</span>
          </NavLink>
          {/* ================= ORDERS ================= */}
          <button
            onClick={() => handleMenuToggle("orders")}
            className={`w-full px-4 py-2.5 rounded-none flex justify-between items-center transition text-sm font-medium
      ${
        ordersOpen
          ? "bg-blue-50 text-blue-900"
          : "text-gray-700 hover:bg-gray-50"
      }
    `}
          >
            <div className="flex items-center gap-3">
              <ClipboardList size={18} />
              <span>Manage Orders</span>
            </div>
            {ordersOpen ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </button>

          <div
            className={`ml-6 border-l border-gray-200 transition-all duration-300 ease-in-out overflow-hidden ${
              ordersOpen ? "max-h-48 opacity-100 mt-0" : "max-h-0 opacity-0 pointer-events-none"
            }`}
          >
            <NavLink
              to="/orders/generate-order"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              Generate Order
            </NavLink>
            <NavLink
              to="/orders/view-order"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              View Orders
            </NavLink>
            <NavLink
              to="/orders/retail-orders"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              Mobile App Orders
            </NavLink>
            <NavLink
              to="/orders/return-order"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              Return Order
            </NavLink>
          </div>

          {/* ================= CLIENTS ================= */}
          <button
            onClick={() => handleMenuToggle("clients")}
            className={`w-full px-4 py-2.5 rounded-none flex justify-between items-center transition text-sm font-medium
      ${
        clientsOpen
          ? "bg-blue-50 text-blue-900"
          : "text-gray-700 hover:bg-gray-50"
      }
    `}
          >
            <div className="flex items-center gap-3">
              <Users size={18} />
              <span>Manage Clients</span>
            </div>
            {clientsOpen ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </button>

          <div
            className={`ml-6 border-l border-gray-200 transition-all duration-300 ease-in-out overflow-hidden ${
              clientsOpen ? "max-h-36 opacity-100 mt-0" : "max-h-0 opacity-0 pointer-events-none"
            }`}
          >
            <NavLink
              to="/manage-client/client"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              Clients
            </NavLink>
            <NavLink
              to="/manage-client/mobile-users"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              Retail Users
            </NavLink>
            <NavLink
              to="/manage-client/follow-up"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              Nutritional Follow Up
            </NavLink>
          </div>

          {/* ================= REPORTS ================= */}
          <button
            onClick={() => handleMenuToggle("reports")}
            className={`w-full px-4 py-2.5 rounded-none flex justify-between items-center transition text-sm font-medium
      ${
        reportsOpen
          ? "bg-blue-50 text-blue-900"
          : "text-gray-700 hover:bg-gray-50"
      }
    `}
          >
            <div className="flex items-center gap-3">
              <BarChart3 size={18} />
              <span>Reports</span>
            </div>
            {reportsOpen ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </button>

          <div
            className={`ml-6 border-l border-gray-200 transition-all duration-300 ease-in-out overflow-hidden ${
              reportsOpen ? "max-h-48 opacity-100 mt-0" : "max-h-0 opacity-0 pointer-events-none"
            }`}
          >
            <NavLink
              to="/reports/sales-reports"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              Sales Reports
            </NavLink>
            <NavLink
              to="/reports/staff-sales"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              Staff Wise Sales
            </NavLink>
            <NavLink
              to="/reports/inventory-reports"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              Inventory Reports
            </NavLink>
            <NavLink
              to="/reports/product-ledger"
              className={({ isActive }) =>
                `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-900 border-l-4"
                    : "text-gray-700 hover:bg-gray-50"
                }`
              }
              style={({ isActive }) =>
                isActive ? { borderLeftColor: "#172554" } : {}
              }
            >
              Product Stock Ledger
            </NavLink>
          </div>

          {/* ================= BOOKS – SUPER ADMIN & ADMIN ================= */}
          {(isSuperAdmin || isAdmin) && (
            <>
              <button
                onClick={() => handleMenuToggle("books")}
                className={`w-full px-4 py-2.5 rounded-none flex justify-between items-center transition text-sm font-medium
          ${
            booksOpen
              ? "bg-blue-50 text-blue-900"
              : "text-gray-700 hover:bg-gray-50"
          }
        `}
              >
                <div className="flex items-center gap-3">
                  <BookOpen size={18} />
                  <span>Books</span>
                </div>
                {booksOpen ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </button>

              <div
                className={`ml-6 border-l border-gray-200 transition-all duration-300 ease-in-out overflow-hidden ${
                  booksOpen ? "max-h-48 opacity-100 mt-0" : "max-h-0 opacity-0 pointer-events-none"
                }`}
              >
                {isSuperAdmin && (
                  <NavLink
                    to="/books/profit-loss"
                    className={({ isActive }) =>
                      `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                        isActive
                          ? "bg-blue-50 text-blue-900 border-l-4"
                          : "text-gray-700 hover:bg-gray-50"
                      }`
                    }
                    style={({ isActive }) =>
                      isActive ? { borderLeftColor: "#172554" } : {}
                    }
                  >
                    Profit & Loss
                  </NavLink>
                )}
                <NavLink
                  to="/books/expenses"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                      isActive
                        ? "bg-blue-50 text-blue-900 border-l-4"
                        : "text-gray-700 hover:bg-gray-50"
                    }`
                  }
                  style={({ isActive }) =>
                    isActive ? { borderLeftColor: "#172554" } : {}
                  }
                >
                  Expenses
                </NavLink>
                {isSuperAdmin && (
                  <NavLink
                    to="/books/sales-summary"
                    className={({ isActive }) =>
                      `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                        isActive
                          ? "bg-blue-50 text-blue-900 border-l-4"
                          : "text-gray-700 hover:bg-gray-50"
                      }`
                    }
                    style={({ isActive }) =>
                      isActive ? { borderLeftColor: "#172554" } : {}
                    }
                  >
                    Sales Summary
                  </NavLink>
                )}
                {isSuperAdmin && (
                  <NavLink
                    to="/books/ledger-collections"
                    className={({ isActive }) =>
                      `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                        isActive
                          ? "bg-blue-50 text-blue-900 border-l-4"
                          : "text-gray-700 hover:bg-gray-50"
                      }`
                    }
                    style={({ isActive }) =>
                      isActive ? { borderLeftColor: "#172554" } : {}
                    }
                  >
                    Ledger & Collections
                  </NavLink>
                )}
              </div>
            </>
          )}

          {/* ================= SETTINGS – SUPER ADMIN ONLY ================= */}
          {isSuperAdmin && (
            <>
              <button
                onClick={() => handleMenuToggle("settings")}
                className={`w-full px-4 py-2.5 rounded-none flex justify-between items-center transition text-sm font-medium
          ${
            settingsOpen
              ? "bg-blue-50 text-blue-900"
              : "text-gray-700 hover:bg-gray-50"
          }
        `}
              >
                <div className="flex items-center gap-3">
                  <Settings size={18} />
                  <span>Settings</span>
                </div>
                {settingsOpen ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </button>

              <div
                className={`ml-6 border-l border-gray-200 transition-all duration-300 ease-in-out overflow-hidden ${
                  settingsOpen ? "max-h-48 opacity-100 mt-0" : "max-h-0 opacity-0 pointer-events-none"
                }`}
              >
                <NavLink
                  to="/settings/company"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                      isActive
                        ? "bg-blue-50 text-blue-900 border-l-4"
                        : "text-gray-700 hover:bg-gray-50"
                    }`
                  }
                  style={({ isActive }) =>
                    isActive ? { borderLeftColor: "#172554" } : {}
                  }
                >
                  Company Settings
                </NavLink>
                <NavLink
                  to="/settings/change-password"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                      isActive
                        ? "bg-blue-50 text-blue-900 border-l-4"
                        : "text-gray-700 hover:bg-gray-50"
                    }`
                  }
                  style={({ isActive }) =>
                    isActive ? { borderLeftColor: "#172554" } : {}
                  }
                >
                  Change Password
                </NavLink>
                <NavLink
                  to="/settings/offers"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                      isActive
                        ? "bg-blue-50 text-blue-900 border-l-4"
                        : "text-gray-700 hover:bg-gray-50"
                    }`
                  }
                  style={({ isActive }) =>
                    isActive ? { borderLeftColor: "#172554" } : {}
                  }
                >
                  Offers
                </NavLink>
                <NavLink
                  to="/settings/loyalty-points"
                  className={({ isActive }) =>
                    `block w-full px-4 py-2 rounded-none text-sm transition-all duration-200 ${
                      isActive
                        ? "bg-blue-50 text-blue-900 border-l-4"
                        : "text-gray-700 hover:bg-gray-50"
                    }`
                  }
                  style={({ isActive }) =>
                    isActive ? { borderLeftColor: "#172554" } : {}
                  }
                >
                  Loyalty Points
                </NavLink>
              </div>
            </>
          )}

          {/* ================= POS ================= */}
        </nav>

        <div className="p-4 bg-white">
          {/* <button
      onClick={handleLogout}
      className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
    >
      Logout
    </button> */}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
